/* Where the pictures are served from (3 Aug 2026, see
   docs/cdn-migration-plan.md).

   The corpus, the importer and every card record deal in one shape of path,
   `/images/practices/attia--zone2.webp`, and always have. This file is the
   only thing that knows those files are no longer beside the app: it prefixes
   the bucket and it knows the variant ladder. That is what keeps the hosting
   decision reversible, because unsetting the base puts every URL back exactly
   the way it was and nothing else has to move.

   Three trees moved, not one. The generated art went first; portraits and the
   rotation backgrounds followed on 3 Aug 2026 (Rob), once it was clear the
   argument for keeping them was thin. What had been said for the portraits was
   that the rights ledger has to sit beside them, and that does not hold up:
   the ledger is a JSON file that stays in the repo either way, it keys on the
   filename, and the filename does not change.

   **The canonical path always holds the original.** Variants live in a width
   directory beside it, so `portraits/128/longo.png` is derived and
   `portraits/longo.png` is the 800px file it came from. That rule is the whole
   answer to the mistake that prompted this: portraits were resized in place
   and the originals survived only in git history, which is not an archive, it
   is a thing we were about to rewrite. Nothing is ever the only copy of
   itself again.

   What did not move: a user's own practice photo, which is private, owner-only
   and served from `/api/uploads` by the app itself. */

interface Ladder {
  /** Generated widths, narrowest first. Empty means the original only. */
  widths: readonly number[];
  /** The width of the file at the canonical path. */
  original: number;
}

/* The ladders, each from measured render widths rather than round numbers.

   images       feed thumb 168 desktop and 358 phone, Crossfire tile 319 to 359
                at every viewport, hero 684 in the app and 1316 on the front
                door. The source is 1200x780 by the generation contract.
   portraits    avatars draw at 20 to 64px, and the public pages carry one at
                120. So 128 covers every one of them at 1x and 256 at 2x, and
                the 800px source stays for whatever wants it later.
   backgrounds  no ladder. A page background is a CSS background, which takes
                no srcset, so a variant here would be a file nothing could ask
                for. They are 1717x916 and about 100KB, and making them
                selectable means making them <img> first, which is a change to
                how the crossfade works and not one to bundle in here. */
const LADDERS: Record<string, Ladder> = {
  "/images/": { widths: [360, 720], original: 1200 },
  "/portraits/": { widths: [128, 256], original: 800 },
  "/backgrounds/": { widths: [], original: 1717 },
};

const PREFIXES = Object.keys(LADDERS);

/** The ladder for a stored path, or null if it is not one of ours. */
export function ladderFor(stored: string): Ladder | null {
  const prefix = PREFIXES.find((p) => stored.startsWith(p));
  return prefix ? (LADDERS[prefix] as Ladder) : null;
}

/** Whether this stored path is one of the files that moved to the bucket. */
export function isAssetPath(stored: string): boolean {
  return ladderFor(stored) !== null;
}

interface Parts {
  dir: string;
  file: string;
  /** The `?v=<hash>` the importer stamped on, carried onto every variant. */
  query: string;
}

function split(stored: string): Parts | null {
  if (!isAssetPath(stored)) return null;
  const q = stored.indexOf("?");
  const query = q === -1 ? "" : stored.slice(q);
  const p = q === -1 ? stored : stored.slice(0, q);
  const slash = p.lastIndexOf("/");
  return { dir: p.slice(0, slash), file: p.slice(slash + 1), query };
}

/** A stored path as the browser should ask for it. */
export function assetUrl(base: string, stored: string): string {
  return base && isAssetPath(stored) ? base + stored : stored;
}

/** The same file at one of the generated widths. */
export function assetVariant(base: string, stored: string, width: number): string {
  const p = split(stored);
  if (!p) return stored;
  return `${base}${p.dir}/${width}/${p.file}${p.query}`;
}

/* The ladder as a srcset, original last so a browser that cannot parse the
   list still lands on something real. Empty for anything that did not move and
   for anything with no variants, which is the signal to the caller to leave
   `srcset` off the element entirely rather than to set it to "". */
export function assetSrcSet(base: string, stored: string): string {
  const ladder = ladderFor(stored);
  if (!ladder || ladder.widths.length === 0) return "";
  return [
    ...ladder.widths.map((w) => `${assetVariant(base, stored, w)} ${w}w`),
    `${assetUrl(base, stored)} ${ladder.original}w`,
  ].join(", ");
}

/* Both halves of an <img>, so a caller writes {...assetImg(base, path)} and
   cannot get one without the other. The `sizes` is the caller's, since only
   the caller knows how wide the thing renders. */
export function assetImg(base: string, stored: string): { src: string; srcSet?: string } {
  const srcSet = assetSrcSet(base, stored);
  return srcSet ? { src: assetUrl(base, stored), srcSet } : { src: assetUrl(base, stored) };
}

/** Every object one published file owns: the original, then its ladder. */
export function assetKeys(stored: string): string[] {
  const ladder = ladderFor(stored);
  const p = split(stored);
  if (!ladder || !p) return [];
  return [
    `${p.dir}/${p.file}`.slice(1),
    ...ladder.widths.map((w) => `${p.dir}/${w}/${p.file}`.slice(1)),
  ];
}
